import { fabric } from 'fabric'
import axios from 'axios'
import { padStart } from 'lodash-es'
import type Merch from '../merch'
import { findParentPath } from '../../utils'
import type { IMerchTemplate, IMerchTemplateFileMapping, IMerchTemplateOption } from './IMerchTemplate'
import type CatalogDetails from '@/models/catalogDetails'
import { appConstants, merchConstants } from '@/models/constants'
import type MyArticle from '@/models/myArticle'
import utils from '@/services/utils'
import apparelTechSheetImage from '@/assets/templateLayoutImages/apparelTechSheet.png'

class ApparelTechSheet implements IMerchTemplate {
  id = merchConstants.slideGenTemplatesId.apparelTechSheet
  name = 'Apparel Tech Sheet'
  layoutImage = apparelTechSheetImage
  imgSize: number = 500
  slideWidth: number = 1280
  slideHeight: number = 720
  imgHorSpacing: number = 40
  imgVerSpacing: number = 20
  logoWidth = 155
  logoHeight = 87
  logoScaling = 0.20
  uaNameLogoWidth = 155
  uaNameLogoHeight = 15
  uaNameLogoScaling = 1
  leftMarginForModel = 30
  startPositionX = 0
  startPositionY = 0
  imageHeight: number = 500
  imageWidth: number = 500
  font: string = 'Neue Plak'
  rightMargin = 20
  imageDefaultScaleFactor: number = 0.9
  leftPadding: number = 50
  rightPadding: number = 30
  topPadding: number = 50
  uaHeaderIconLogoData: string = ''
  uaHeaderNameLogoData: string = ''
  articleImageProps = {
    bottomPadding: 50,
  }

  modelNameProps = {
    width: 325,
    fontSize: 54,
    fontWeight: 'bold',
    topPadding: 120,
    leftPadding: 120,
    bottomPadding: 30,
    rightPadding: 30,
    textAlign: 'left',
    lock: false,
    fill: '#ffffff',
  }

  modelDetailsProps = {
    padding: 5,
    fontSize: 14,
    width: 325,
    fontWeight: 'normal',
    textAlign: 'left',
    lock: false,
    fill: '#ffffff',
  }

  headerImageNameLogoProps = {
    bottomPadding: 180,
  }

  textBoxProps = {
    width: 325,
    topPadding: 20,
    bottomPadding: 10,
    leftPadding: 120,
    fontSize: 14,
    fontWeight: 'normal',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  needToKnowstextBoxProps = {
    width: 310,
    topPadding: 10,
    bottomPadding: 130,
    leftPadding: 120,
    fontSize: 34,
    fontWeight: 'bold',
    textAlign: 'left',
    fill: '#ffffff',
    fontStyle: 'normal',
  }

  ponitsTextBoxProps = {
    topPadding: 40,
    leftPadding: 350,
  }

  attributesProps = {
    width: 325,
  }

  getOptions(catalog: CatalogDetails, myAttributes: Record<string, IMyAttribute>, defaultImageTypeForTemplates: string): IMerchTemplateOption[] {
    const options: IMerchTemplateOption[] = []
    const modelAttributesWithSKU: IMyAttribute[] = []
    const defaultDisplayAttributesList = ['Department', 'Class', 'ModelLifecycle']
    const defaultAllowedDisplayAttributesList = defaultDisplayAttributesList.filter(attribute => myAttributes[attribute])
    defaultAllowedDisplayAttributesList.push('currentSKuCount')
    // model attributes
    const modellevelAttributes = Object.values(myAttributes).filter(attr => attr.IsModelLevel)
    for (const attr of modellevelAttributes) {
      modelAttributesWithSKU.push(attr)
    }

    // add current season SKU count
    const SKUField = { ...appConstants.staticFieldTemplate }
    SKUField.DisplayName = `${catalog.Season} SKU`
    SKUField.SystemName = 'currentSKuCount'
    SKUField.IsModelLevel = true
    SKUField.IsSKU = true
    modelAttributesWithSKU.push(SKUField)

    options.push({
      name: 'displayAttributes',
      label: 'generateFrameDialog.steps.options.modelAttributesToDisplay',
      type: 'attributeChooser',
      default: defaultAllowedDisplayAttributesList,
      required: false,
      clearable: true,
      filterable: true,
      multiple: true,
      multipleLimit: 5,
      data: modelAttributesWithSKU,
      valueProp: 'SystemName',
      displayProp: 'DisplayName',
    })

    options.push({
      name: 'useModelObject',
      label: 'generateDialog.steps.options.useModelObject',
      type: 'bool',
      required: false,
      default: true,
    })

    options.push({
      name: 'imageType',
      label: 'generateFrameDialog.steps.options.imageType',
      type: 'list',
      default: defaultImageTypeForTemplates,
      data: merchConstants.defaultImageType,
      required: false,
      multiple: false,
      valueProp: 'key',
      displayProp: 'value',
      isMerch: true,
    })

    // const availableFrameSizes: { key: FrameSize, label: string }[] = [
    //   { key: 'wide', label: 'Wide' },
    //   { key: 'a4', label: 'A4' },
    //   { key: 'letter', label: 'Letter' },
    // ]

    // options.push({
    //   name: 'frameSize',
    //   label: 'generateFrameDialog.steps.options.frameSize',
    //   type: 'list',
    //   default: 'wide',
    //   required: true,
    //   multiple: false,
    //   data: availableFrameSizes,
    //   valueProp: 'key',
    //   displayProp: 'label',
    // })

    return options
  }

  getFileMapping(): IMerchTemplateFileMapping[] {
    return [{
      name: 'articleNumber',
      column: 'ArticleNumber',
      type: 'articleNumber',
      label: 'generateFrameDialog.steps.mapping.articleNumber',
      required: true,
      autoMap: ['article', 'articlenumber', 'article number', 'artnbr', 'style color code'],
    }]
  }

  async generate(catalog: CatalogDetails, articlesData: MyArticle[], options: Record<string, any>, merch: Merch | undefined, myAttributes: Record<string, IMyAttribute>) {
    const [uaHeaderLogoData, uaHeaderNameData]: any = await this.getHeaderLogosData([
      appConstants.logoPaths.uaIconWhite,
      appConstants.logoPaths.uaNameLogoWhite,
    ])

    this.uaHeaderIconLogoData = uaHeaderLogoData
    this.uaHeaderNameLogoData = uaHeaderNameData
    return await this.buildTemplate(catalog, articlesData, options, myAttributes, merch)
  }

  async buildTemplate(catalog: CatalogDetails, articles: MyArticle[], options: Record<string, any>, myAttributes: Record<string, IMyAttribute>, merch: Merch | undefined) {
    const promises: Promise<void | number>[] = []
    this.imgSize = this.imageWidth * this.imageDefaultScaleFactor
    const slideSize = options.slideSize
    const articleAttributes = options.displayAttributes

    this.changePropsAsPerSlideSize(slideSize)

    const modelsMap = new Set<string>()
    const uniqueModelArticles: MyArticle[] = []
    for (const article of articles) {
      if (!modelsMap.has(article.ModelNumber)) {
        modelsMap.add(article.ModelNumber)
        uniqueModelArticles.push(article)
      }
    }

    const verticalSectionWidth = this.slideWidth / 2
    const horizontalSectionByHeight = this.slideHeight / 2

    let slideObjects: any[] = []
    let ttlSlides = 0
    for (const firstArticle of uniqueModelArticles) {
      slideObjects.push(await this.createHeaderLogo(
        this.leftPadding + (this.logoWidth * this.logoScaling) / 2,
        this.topPadding + (this.logoHeight * this.logoScaling) / 2,
      ))

      slideObjects.push(await this.createHeaderNameLogo(
        this.slideHeight - this.headerImageNameLogoProps.bottomPadding + (this.uaNameLogoWidth * this.uaNameLogoScaling) / 2,
        this.leftPadding,
      ))

      const modelNameHeight = this.getTextRequiredHeightBasedOnWidth(firstArticle.ModelName, verticalSectionWidth - this.modelNameProps.leftPadding, this.font, this.modelNameProps.fontSize, this.modelNameProps.fontWeight)
      const modelNameLeft = this.modelNameProps.leftPadding
      const modelNameTop = this.modelNameProps.topPadding
      const modelNameTextObject = this.createLabel(
        firstArticle,
        ['ModelName'],
        '',
        modelNameTop,
        modelNameLeft,
        verticalSectionWidth - this.modelNameProps.rightPadding,
        0,
        this.modelNameProps.fontSize,
        this.modelNameProps.fontWeight,
        'left',
        '#ffffff',
        options.useModelObject,
      )
      slideObjects.push(modelNameTextObject)

      const modelDetailsHeight = this.getTextRequiredHeightBasedOnWidth(`${firstArticle.ModelNumber} // ${firstArticle._RetailPrice}`, this.modelDetailsProps.width, this.font, this.modelDetailsProps.fontSize, this.modelDetailsProps.fontWeight)
      const modelDetailsTop = modelNameTop + modelNameHeight + this.modelDetailsProps.padding
      const modelDetailsLeft = modelNameLeft
      const modelDetailsTextObject = this.createLabel(
        firstArticle,
        ['ModelNumber', '_RetailPrice'],
        ' // ',
        modelDetailsTop,
        modelDetailsLeft,
        verticalSectionWidth - this.modelNameProps.leftPadding,
        0,
        this.modelDetailsProps.fontSize,
        this.modelDetailsProps.fontWeight,
        'left',
        '#ffffff',
        options.useModelObject,
      )
      slideObjects.push(modelDetailsTextObject)

      if (articleAttributes.length !== 0) {
        const attributeObjectTop = modelDetailsTop + modelDetailsHeight + this.modelDetailsProps.padding
        slideObjects.push(
          this.createLabel(
            firstArticle,
            articleAttributes,
            '\n',
            attributeObjectTop,
            modelDetailsLeft,
            verticalSectionWidth - this.modelNameProps.leftPadding,
            0,
            this.modelDetailsProps.fontSize,
            this.modelDetailsProps.fontWeight,
            'left',
            '#ffffff',
            options.useModelObject,
          ),
        )
      }

      const needToKnowsTextBoxTop = horizontalSectionByHeight + this.needToKnowstextBoxProps.topPadding
      const needToKnowsHeight = this.getTextRequiredHeightBasedOnWidth('NEED TO KNOWS', this.needToKnowstextBoxProps.width, this.font, this.needToKnowstextBoxProps.fontSize, this.needToKnowstextBoxProps.fontWeight)
      const needToKnowsTextBox = this.createTextObject(
        'NEED TO KNOWS',
        needToKnowsTextBoxTop,
        modelDetailsLeft,
        verticalSectionWidth - this.needToKnowstextBoxProps.leftPadding,
        needToKnowsHeight,
        this.needToKnowstextBoxProps.fontSize,
        this.needToKnowstextBoxProps.fontStyle,
        this.needToKnowstextBoxProps.fontWeight,
        this.needToKnowstextBoxProps.textAlign,
        this.needToKnowstextBoxProps.fill,
        false,
      )
      slideObjects.push(needToKnowsTextBox)

      const pointsTextBoxTop = needToKnowsTextBoxTop + this.ponitsTextBoxProps.topPadding
      const pointsTextBox = this.createTextObject(
        'Enter text here',
        pointsTextBoxTop,
        modelDetailsLeft,
        verticalSectionWidth - this.textBoxProps.leftPadding,
        needToKnowsHeight,
        this.textBoxProps.fontSize,
        this.textBoxProps.fontStyle,
        this.textBoxProps.fontWeight,
        this.textBoxProps.textAlign,
        this.textBoxProps.fill,
        false,
      )
      slideObjects.push(pointsTextBox)

      const imgLeft = this.slideWidth / 2 + verticalSectionWidth / 2
      const imgTop = this.slideHeight / 2
      slideObjects.push(
        this.createImage(
          firstArticle,
          imgLeft,
          imgTop,
          this.imageDefaultScaleFactor,
          this.imageDefaultScaleFactor,
          options.imageType,
          options.useModelObject,
        ),
      )

      ttlSlides++
      promises.push(this.createSlide(merch, `${firstArticle.ModelNumber} - ${firstArticle.ModelName}`, slideObjects.slice(), options, this.imageDefaultScaleFactor)
        .catch((error) => {
          console.warn(`Create slide failed, unable to create slide object file, \n${error}`)
        }))

      slideObjects = []
    }

    return Promise.all(promises)
      .then(() => {
        return ttlSlides
      })
  }

  changePropsAsPerSlideSize(slideSize: string) {
    this.slideHeight = merchConstants.slideSizeCanvasDimensions[slideSize].height
    this.slideWidth = merchConstants.slideSizeCanvasDimensions[slideSize].width
  }

  createSlide(merch, slideName, objects: any[], options: Record<string, any>, imageDefaultScaleFactor: number, articleIdList: number[] = []) {
    const folderNode = options.folder
    const sortOrder = Math.max(...folderNode.children.map(child => child.sortOrder), 0) + 1
    const folderStructure = findParentPath(folderNode)
    if (utils.isDefined(folderNode.children.find(node => node.label === slideName))) {
      let c = 0
      do {
        c++
        slideName = `${slideName} (${c})`
      } while (folderNode.children.find(node => node.label === slideName) !== undefined)
    }
    return merch!.addSlide(folderStructure.id, folderStructure.name, folderNode.sortOrder, slideName, objects, options.slideSize, imageDefaultScaleFactor, sortOrder, true, articleIdList, 'rgba(0, 0, 0, 1)')
  }

  getTextRequiredHeightBasedOnWidth(text: string, availableWidth: number, font: string, fontSize: number, fontWeight: string) {
    const minNumberOfLines = Math.ceil(utils.getTextWidth(text, `${fontWeight} ${fontSize}px ${font}`) / availableWidth)
    let multiLineSampleText = ''
    for (let index = 0; index < minNumberOfLines; index++) {
      multiLineSampleText += text
      if (index < minNumberOfLines - 1) {
        multiLineSampleText += '\n'
      }
    }
    return utils.getTextHeight(multiLineSampleText, fontSize, font, fabric)
  }

  async getHeaderLogosData(urls: string[]) {
    const loadImage = (url: string) => {
      return new Promise<string>((resolve) => {
        axios.get(url, { responseType: 'arraybuffer' }).then((res) => {
          const base64 = res.data ? `data:image/png;base64,${utils.arrayBufferToBase64(res.data)}` : null
          if (base64) {
            const settings = {
              width: this.logoWidth,
              height: this.logoHeight,
              scaleX: this.logoScaling,
              scaleY: this.logoScaling,
            }
            const image = new Image()
            image.onload = () => {
              resolve(utils.resizeImageAndGetBase64(image, settings) as string)
            }
            image.onerror = () => resolve('')
            image.src = base64
          }
          else {
            resolve('')
          }
        }).catch((err) => {
          console.log(err)
          resolve('')
        })
      })
    }

    const imagePromises = urls.map(loadImage)
    return Promise.all(imagePromises)
  }

  async createHeaderLogo(top: number, left: number) {
    return {
      type: merchConstants.objectTypes.image,
      top,
      left,
      width: this.logoWidth,
      height: this.logoHeight,
      angle: 90,
      scaleY: this.logoScaling,
      scaleX: this.logoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.uaHeaderIconLogoData,
    }
  }

  async createHeaderNameLogo(top: number, left: number) {
    return {
      type: merchConstants.objectTypes.image,
      top,
      left,
      width: this.uaNameLogoWidth,
      height: this.uaNameLogoHeight,
      angle: 90,
      scaleY: this.uaNameLogoScaling,
      scaleX: this.uaNameLogoScaling,
      flipX: false,
      flipY: false,
      locked: true,
      templateObject: true,
      data: this.uaHeaderNameLogoData,
    }
  }

  createLabel(article: MyArticle, attributes: string[], articlePropsSeparator: string, top: number, left: number, width: number, height: number, fontSize: number, fontWeight: string, textAlign: string, fill: string, useModelObject: boolean) {
    if (!Array.isArray(attributes)) {
      attributes = [attributes]
    }
    if (!attributes || attributes.length === 0) { return }

    const obj: Record<string, any> = {
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      fill,
      flipX: false,
      flipY: false,
      fontFamily: this.font,
      fontSize,
      fontStyle: 'normal',
      fontWeight,
      textAlign,
      articleId: article.Id,
      article,
      showModelAttributesAndCurrentSeasonSKUCount: true,
    }

    if (!article._IsRequestArticle && useModelObject) {
      obj.type = merchConstants.objectTypes.modelDetails
      obj.attributes = attributes
      obj.attributesSeparator = articlePropsSeparator
      obj.modelId = article.ModelId
    }
    else {
      obj.type = merchConstants.objectTypes.articleDetails
      obj.customOptions = {
        articleProps: attributes.slice(),
        articleBLCoords: { x: 0, y: 0 },
        separator: articlePropsSeparator,
      }
      obj.isRequest = article._IsRequestArticle
    }

    return obj
  }

  createTextObject(text: string, top: number, left: number, width: number, height: number, fontSize: number, fontStyle: string, fontWeight: string, textAlign: string, fill: string, templateObject: boolean) {
    return {
      type: merchConstants.objectTypes.textBox,
      top,
      left,
      width,
      height,
      angle: 0,
      scaleX: 1,
      scaleY: 1,
      flipX: false,
      flipY: false,
      text,
      fill,
      backgroundColor: '',
      fontFamily: this.font,
      fontSize,
      fontStyle,
      fontWeight,
      textAlign,
      locked: false,
      templateObject,
      isOriginTopLeft: true,
    }
  }

  createImage(article: MyArticle, left: number, top: number, scaleX: number, scaleY: number, imageType: string, useModelObject: boolean) {
    const obj: Record<string, any> = {
      top,
      left,
      width: this.imageWidth,
      height: this.imageHeight,
      articleId: article.Id,
      angle: 0,
      scaleX,
      scaleY,
      flipX: false,
      flipY: false,
      locked: false,
      shouldAllowTemplateBigImagesToResize: true,
      imageType,
      article,
    }
    if (!article._IsRequestArticle && useModelObject) {
      obj.type = merchConstants.objectTypes.modelImage
      obj.modelId = article.ModelId
    }
    else {
      obj.type = merchConstants.objectTypes.articleImage
      obj.isRequest = article._IsRequestArticle
    }

    return obj
  }
}

export default new ApparelTechSheet()
